import type { KategoriaMarzeItem, PoleMarze } from "@/api/hooks/useCennik";
import { formatKategoria } from "@/lib/kategorie";

/**
 * Rozdelenie panelu Nastavenia cien (KOLO 166) — karty podľa sekcie
 * zo servera (`item.sekcia`) a mernej jednotky práce.
 *
 * Karta je to, čo „Nastaviť celej sekcii" mení naraz, preto sa delí aj
 * podľa druhu: práca plošných je €/m², dĺžkových €/m — jedno číslo pre
 * oboje by dĺžkovým dalo nezmysel. Sklo server nevracia.
 */

/** Predvoľba druhu v appke — číslo pre kategórie, ktoré ešte riadok nemajú. */
export const PREDVOLBA = "_DEFAULT";

export type KartaMarzi = {
  kluc: "plosne" | "dlzkove" | "hrany" | "kovanie" | "kovanie_m" | "tovar";
  nazov: string;
  /** Jednotka práce v karte. */
  jednotka: string;
  /** Stĺpce, ktoré karta ukazuje a mení. */
  polia: PoleMarze[];
  polozky: KategoriaMarzeItem[];
};

/** Blok riadkov v karte — samostatná kategória alebo skupina kovania. */
export type BlokMarzi =
  | { typ: "riadok"; polozka: KategoriaMarzeItem }
  | { typ: "skupina"; kod: string; clenovia: KategoriaMarzeItem[] }
  | { typ: "nadpis"; text: string };

/** Jednotka práce podľa druhu položky (práca je na MJ). */
export const JEDNOTKA_PRACE: Record<string, string> = {
  plosny: "€/m²",
  dlzkovy: "€/m",
  kusovy: "€/ks",
};

const BEZ_ODPADU: PoleMarze[] = ["praca_eur_per_mj", "rezia_pct"];

export function nazovKategorie(kategoria: string): string {
  return kategoria === PREDVOLBA
    ? "Predvoľba pre nové kategórie"
    : formatKategoria(kategoria);
}

/** Pracovné dosky a zásteny sú podskupina Materiálov (nie vlastná sekcia). */
export function jePracovnaDoska(kategoria: string): boolean {
  return /^(PRACOVNA_DOSKA|ZASTENA)/.test(kategoria);
}

function podlaNazvu(a: KategoriaMarzeItem, b: KategoriaMarzeItem): number {
  return nazovKategorie(a.kategoria).localeCompare(
    nazovKategorie(b.kategoria),
    "sk",
    { numeric: true },
  );
}

export function kartyMarzi(data: readonly KategoriaMarzeItem[]): KartaMarzi[] {
  const materialy = data.filter((d) => d.sekcia === "materialy");
  const kovanie = data.filter((d) => d.sekcia === "kovanie");
  const karty: KartaMarzi[] = [
    {
      kluc: "plosne",
      nazov: "Plošné dosky",
      jednotka: JEDNOTKA_PRACE.plosny!,
      polia: ["praca_eur_per_mj", "rezia_pct", "nadmiera_pct", "nadmiera_smer_pct"],
      polozky: materialy.filter((d) => d.druh === "plosny"),
    },
    {
      kluc: "dlzkove",
      nazov: "Dĺžkové materiály",
      jednotka: JEDNOTKA_PRACE.dlzkovy!,
      polia: ["praca_eur_per_mj", "rezia_pct", "nadmiera_pct"],
      polozky: materialy.filter((d) => d.druh !== "plosny"),
    },
    {
      kluc: "hrany",
      nazov: "Hrany",
      jednotka: JEDNOTKA_PRACE.dlzkovy!,
      polia: BEZ_ODPADU,
      polozky: data.filter((d) => d.sekcia === "hrany"),
    },
    {
      kluc: "kovanie",
      nazov: "Kovanie",
      jednotka: JEDNOTKA_PRACE.kusovy!,
      polia: BEZ_ODPADU,
      polozky: kovanie.filter((d) => d.druh === "kusovy"),
    },
    {
      // Krytky, profily, tyče v metroch — vlastná cena za meter,
      // skupinová cena kusového kovania ich nemení
      kluc: "kovanie_m",
      nazov: "Kovanie v metroch",
      jednotka: JEDNOTKA_PRACE.dlzkovy!,
      polia: BEZ_ODPADU,
      polozky: kovanie.filter((d) => d.druh !== "kusovy"),
    },
    {
      // Tovar (KOLO 230) — cena z cenníka dodávateľa je predajná, réžia
      // a práca 0; prirážku obchodu nastaví user tu
      kluc: "tovar",
      nazov: "Tovar (regály)",
      jednotka: JEDNOTKA_PRACE.kusovy!,
      polia: BEZ_ODPADU,
      polozky: data.filter((d) => d.sekcia === "tovar"),
    },
  ];
  return karty
    .filter((k) => k.polozky.length > 0)
    .map((k) => ({ ...k, polozky: [...k.polozky].sort(podlaNazvu) }));
}

/**
 * Riadky karty: kovanie po skupinách (`item.skupina` zo servera),
 * materiály s podskupinou Pracovné dosky, predvoľba na konci.
 */
export function blokyKarty(karta: KartaMarzi): BlokMarzi[] {
  const predvolby = karta.polozky.filter((p) => p.kategoria === PREDVOLBA);
  const ostatne = karta.polozky.filter((p) => p.kategoria !== PREDVOLBA);
  const bloky: BlokMarzi[] = [];

  if (karta.kluc === "kovanie" || karta.kluc === "kovanie_m") {
    const skupiny = new Map<string, KategoriaMarzeItem[]>();
    for (const p of ostatne) {
      const kod = p.skupina ?? p.kategoria;
      skupiny.set(kod, [...(skupiny.get(kod) ?? []), p]);
    }
    const zoradene = [...skupiny.entries()].sort(([a], [b]) =>
      nazovKategorie(a).localeCompare(nazovKategorie(b), "sk", { numeric: true }),
    );
    for (const [kod, clenovia] of zoradene) {
      const sama = clenovia.length === 1 && clenovia[0]!.kategoria === kod;
      bloky.push(
        sama
          ? { typ: "riadok", polozka: clenovia[0]! }
          : {
              typ: "skupina",
              kod,
              // rodič („priamo v skupine") prvý, potom podskupiny
              clenovia: [...clenovia].sort((a, b) =>
                a.kategoria === kod ? -1 : b.kategoria === kod ? 1 : 0,
              ),
            },
      );
    }
  } else {
    const dosky = ostatne.filter((p) => jePracovnaDoska(p.kategoria));
    for (const p of ostatne) {
      if (!jePracovnaDoska(p.kategoria)) bloky.push({ typ: "riadok", polozka: p });
    }
    if (dosky.length > 0) {
      bloky.push({ typ: "nadpis", text: "Pracovné dosky a zásteny" });
      for (const p of dosky) bloky.push({ typ: "riadok", polozka: p });
    }
  }

  // Predvoľba na konci — číslo pre kategórie, ktoré ešte riadok nemajú
  for (const p of predvolby) bloky.push({ typ: "riadok", polozka: p });
  return bloky;
}
